import React from 'react';
import { motion } from 'motion/react';

import {
  Brain,
  Bot,
  BarChart3,
  Cpu,
  Sparkles,
  Zap,
  MessageSquare,
  Eye,
  CheckCircle,
  ArrowRight,
  Target,
  TrendingUp,
  Shield,
  Cog,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SpamProtectedForm from '../../components/SpamProtectedForm';
import '../../styles/services.css';

const features = [
  { icon: Bot, title: 'AI Chatbots', desc: 'Intelligent conversational agents for customer engagement' },
  { icon: BarChart3, title: 'Predictive Analytics', desc: 'Data-driven insights for smarter business decisions' },
  { icon: Eye, title: 'Computer Vision', desc: 'Image and video analysis solutions' },
  { icon: MessageSquare, title: 'NLP Solutions', desc: 'Natural language processing for text analysis' },
  { icon: Cog, title: 'Process Automation', desc: 'AI-powered workflow automation' },
  { icon: Sparkles, title: 'Custom AI Models', desc: 'Tailored machine learning models for your needs' },
];

const useCases = [
  { title: 'Customer Service', desc: 'AI chatbots and virtual assistants' },
  { title: 'Document Processing', desc: 'Automated data extraction and analysis' },
  { title: 'Fraud Detection', desc: 'Real-time anomaly detection systems' },
  { title: 'Recommendation Engines', desc: 'Personalized product and content recommendations' },
];

const AISolutionsPage = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="service-hero ai">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="service-hero-content"
          >
            <span className="service-badge">
              <Brain size={16} /> Artificial Intelligence
            </span>
            <h1>Unlock the Power of AI</h1>
            <p>Transform your business with cutting-edge artificial intelligence solutions. From chatbots to predictive analytics, we build AI that delivers real results.</p>
            <div className="hero-buttons">
              <a href="#contact" className="btn-service-primary">
                Explore AI Solutions <ArrowRight size={18} />
              </a>
              <a href="#features" className="btn-service-secondary">
                Our Capabilities
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="service-features" id="features">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">AI Capabilities</span>
            <h2>Our AI Solutions</h2>
            <p>Enterprise-grade AI solutions tailored to your business needs</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="feature-card"
              >
                <div className="feature-icon ai">
                  <feature.icon size={24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="service-usecases">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">Use Cases</span>
            <h2>AI in Action</h2>
            <p>Real-world applications that drive business value</p>
          </div>
          
          <div className="usecases-grid">
            {useCases.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="usecase-card"
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="service-contact" id="contact">
        <div className="container-custom">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2>Start Your AI Journey</h2>
              <p>Let's explore how AI can transform your business operations.</p>
              <ul className="contact-benefits">
                <li><CheckCircle size={20} /> Free AI readiness assessment</li>
                <li><CheckCircle size={20} /> Custom solution design</li>
                <li><CheckCircle size={20} /> Proof of concept development</li>
                <li><CheckCircle size={20} /> Full implementation support</li>
              </ul>
            </div>
            
            <SpamProtectedForm 
              emailSubject="AI Solutions Inquiry"
              submitText="Send Message"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AISolutionsPage;
